import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../../api/client';

const CATEGORY_OPTIONS = [
  { value: 'music', label: 'Música' },
  { value: 'program', label: 'Programa' },
  { value: 'advertising', label: 'Publicidad' },
  { value: 'jingle', label: 'Jingle' },
  { value: 'promo', label: 'Promo' },
  { value: 'fx', label: 'FX / Sweep' },
  { value: 'playlist', label: 'Playlist' },
  { value: 'special', label: 'Especial' },
  { value: 'fallback', label: 'Fallback' },
];

export default function FolderForm({ editMode = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = editMode && Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    category: 'music',
    description: '',
    color: '#27272a',
    image_url: '',
    is_active: true,
  });

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState('');

  const handleImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX = 320;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
        setImagePreview(dataUrl);
        setFormData((prev) => ({ ...prev, image_url: dataUrl }));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (isEdit) {
      fetchFolder();
    }
  }, [id]);

  const fetchFolder = async () => {
    try {
      const { data } = await api.get(`/api/music/folders/${id}`);
      setFormData({
        name: data.name || '',
        category: data.category || 'music',
        description: data.description || '',
        color: data.color || '#27272a',
        image_url: data.image_url || '',
        is_active: data.is_active ?? true,
      });
      if (data.image_url) setImagePreview(data.image_url);
    } catch (err) {
      console.error('[FolderForm] Fetch failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'El nombre es obligatorio';
    if (!formData.category) newErrors.category = 'Elegí una categoría';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = { ...formData, color: formData.color || '#27272a' };
      if (isEdit) {
        await api.put(`/api/music/folders/${id}`, payload);
      } else {
        await api.post('/api/music/folders', payload);
      }
      navigate('/music/folders');
    } catch (err) {
      console.error('[FolderForm] Save failed:', err);
      setErrors({ submit: err.response?.data?.message || 'Error al guardar la carpeta.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Cargando carpeta...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">
          {isEdit ? 'Editar Carpeta' : 'Crear Carpeta'}
        </h1>
        <Link to="/music/folders" className="btn btn-secondary btn-sm">
          Volver a Carpetas
        </Link>
      </div>

      {errors.submit && (
        <div className="rounded-lg bg-error-900/30 border border-error-700 p-3 text-sm text-error-400">
          {errors.submit}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Nombre *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`input ${errors.name ? 'border-error-500' : ''}`}
              placeholder="Ej: Techno, House, Set de DJ Fulano..."
            />
            {errors.name && <p className="text-xs text-error-400 mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Categoría *</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className={`input ${errors.category ? 'border-error-500' : ''}`}
            >
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <p className="text-xs text-dark-500 mt-1">Las carpetas de música por estilo usan "Música".</p>
            {errors.category && <p className="text-xs text-error-400 mt-1">{errors.category}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-dark-300 mb-1">Descripción</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            className="input min-h-[60px]"
            placeholder="Descripción opcional..."
          />
        </div>

        <div className="rounded-xl bg-dark-850 border border-dark-700 p-4">
          <label className="block text-sm font-medium text-dark-300 mb-2">Foto de la carpeta (guía)</label>
          <div className="flex items-center gap-4">
            <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-lg bg-dark-800 overflow-hidden border border-dark-700">
              {imagePreview ? (
                <img src={imagePreview} alt="preview" className="h-full w-full object-cover" />
              ) : (
                <span className="text-2xl text-dark-500">📁</span>
              )}
            </div>
            <label className="cursor-pointer">
              <span className="btn btn-secondary btn-sm">
                {imagePreview ? 'Cambiar foto' : 'Subir foto'}
              </span>
              <input type="file" accept="image/*" onChange={handleImage} className="hidden" />
            </label>
            {imagePreview && (
              <button type="button" onClick={() => { setImagePreview(''); setFormData((p) => ({ ...p, image_url: '' })); }} className="text-xs text-error-400 hover:text-error-300">
                Quitar
              </button>
            )}
          </div>
          <p className="text-xs text-dark-500 mt-2">Se comprime automáticamente a baja calidad (es solo de referencia).</p>
        </div>

        <div className="flex items-center gap-4">
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                name="color"
                value={formData.color}
                onChange={handleChange}
                className="h-9 w-9 cursor-pointer rounded border border-dark-700 bg-transparent p-0"
              />
              <span className="text-xs text-dark-400">{formData.color}</span>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-6">
            <input
              type="checkbox"
              id="is_active"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="h-4 w-4 rounded border-dark-700 text-primary-600 focus:ring-primary-600"
            />
            <label htmlFor="is_active" className="text-sm text-dark-300">Activa</label>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-dark-700 pt-6">
          <Link to="/music/folders" className="btn btn-secondary">Cancelar</Link>
          <button type="submit" disabled={saving} className="btn btn-primary">
            {saving ? 'Guardando...' : isEdit ? 'Actualizar Carpeta' : 'Crear Carpeta'}
          </button>
        </div>
      </form>
    </div>
  );
}
